import { DocumentReviewState } from './document';

export type UploadUrlResult = {
  uploadUrl: string;
  destination: string;
};

export type ReferralSmsNotification = {
  type: 'partner-referral';
  sentAt: string;
};

export type ReferralResultResponse = {
  referral: {
    clinicCode: string;
    clinicPatientId: string;
    clinicReferralId: string;
    member: {
      address: string;
      dateOfBirth: string;
      fullName: string;
      gender: string;
      insurance: {
        carrier: string;
        groupNumber: string;
        policyNumber: string;
      };
      smsNotifications?: ReferralSmsNotification[];
      telephone: {
        type: string;
        number: string;
      }[];
    };
    referralDate: string;
    referringProvider: {
      fullName: string;
    };
  };
};

export type ReferralNotEngagedReferralsResponse = {
  referrals: {
    clinicReferralId: string;
    memberFullName: string;
    referringFullName: string;
    smsSentAt: string;
    salesforceId?: string;
    referralDate: string;
    phone: string;
    clinic: string;
  }[];
};

export type Referral = {
  id: string;
  memberFullName: string;
  referringFullName: string;
  smsSentAt?: string;
  salesforceId?: string;
  phone: string;
  clinic: string;
  leadsquaredId?: string | null;
  resolvedBy?: string | null;
};

export type NotEngagedReferrals = {
  [referralId: string]: Referral;
};

export interface TelephoneEntry {
  type: string;
  number: string;
}

export interface ReferralDemographics {
  firstName?: string;
  lastName?: string;
  fullName?: string;
  dateOfBirth?: string;
  gender?: string;
  street?: string;
  city?: string;
  state?: string;
  email?: string;
  zipCode?: string;
  telephone?: TelephoneEntry[];
  bmi?: number | null;
  // `null` clears a stored measurement; see `buildReferralUpdateRequest`.
  heightFeet?: number | null;
  heightInch?: number | null;
  weightLb?: number | null;
}

/**
 * How a referral's BMI was obtained (leads-api 0.64.0, #140). Precedence: a BMI the document
 * stated, then one derived from height and weight, then the midpoint of an adult ICD-10 Z68.x
 * code, then the midpoint of a BMI range the document writes (`stated_range`, leads-api #141),
 * then an open-ended bound it writes such as "BMI 50 or higher" (`stated_open_range`, #144).
 * The last three are estimates and need review. Absent on an older API; `null` on a referral
 * stored before 0.64.0 or with no BMI.
 */
export type ReferralBmiSource =
  'stated' | 'measurements' | 'icd10' | 'stated_range' | 'stated_open_range';

/**
 * BMI provenance on a referral, as leads-api returns it on the document detail and on the ops
 * referrals list and detail. Both fields are optional: an API before 0.64.0 sends neither, and
 * the UI then behaves as it did before them.
 */
export interface ReferralBmiProvenance {
  bmiSource?: ReferralBmiSource | null;
  /**
   * `true` when the document gave neither a usable BMI nor a complete height and weight, an
   * ICD-10 estimate included. `null` on a referral stored before 0.64.0: never flagged.
   */
  bmiNeedsReview?: boolean | null;
}

/** A member can carry a third policy; the API accepted only a pair until 0.61.0. */
export const INSURANCE_COVERAGE_ORDER = ['primary', 'secondary', 'tertiary'] as const;
export type InsuranceCoverageOrder = (typeof INSURANCE_COVERAGE_ORDER)[number];

/**
 * One coverage on a referral.
 *
 * The first four are what propagates to LeadSquared and the eligibility handoff. Everything
 * after is coverage detail the referral document carried — the payer's NAIC code, the plan
 * type, the subscriber as a person distinct from the patient, the coverage window. It used
 * to be dropped, which is why billing opened the source PDF by hand.
 */
export interface ReferralInsurance {
  type: InsuranceCoverageOrder;
  carrier?: string;
  policyNumber?: string;
  groupNumber?: string;
  policyHolder?: string;
  payerName?: string;
  payerId?: string;
  planName?: string;
  planType?: string;
  memberId?: string;
  subscriberId?: string;
  groupId?: string;
  groupName?: string;
  subscriberName?: string;
  subscriberDateOfBirth?: string;
  subscriberAddress?: string;
  relationshipToSubscriber?: string;
  coverageStart?: string;
  coverageEnd?: string;
}

/** Where a referral edit got to, per downstream system. */
export type PropagationStatus = 'pending' | 'synced' | 'failed' | 'skipped';

export interface PropagationOutcome {
  status: PropagationStatus;
  at: string;
  attempts: number;
  fields?: string[];
  error?: string;
  reason?: string;
}

export type ReferralPropagationState = Partial<
  Record<'crm' | 'eligibility' | 'core', PropagationOutcome>
>;

export interface UpdateReferralRequest {
  clinicId?: number;
  demographics?: ReferralDemographics;
  insurance?: ReferralInsurance[];
  userId?: number;
  isCommunityReferral?: boolean;
  /** Corrects a mistagged cohort. leads-api forwards it to core, which pushes it to the CRM. */
  systemSource?: string;
  referringProvider?: string;
  communityReferralClinicName?: string;
}

export interface DiscardReferralResponse {
  success: boolean;
  referralId: number;
  discardedAt: string;
  discardReason: string;
}

export interface UpdateReferralResponse {
  success: boolean;
  referralId: number;
  updatedFields: string[];
  message: string;
}

/** One row of the ops Referrals list, from leads-api `GET /v1/ops/referrals`. */
export interface OpsReferral {
  id: number;
  /** The source document; the referral opens next to it. Null for a legacy intake. */
  documentId: number | null;
  clinicId: number | null;
  clinicReferralId: string | null;
  patientName: string;
  crmId: string | null;
  systemSource: string | null;
  isCommunityReferral: boolean;
  status: string;
  eligibilityStatus: string | null;
  engagementStatus: string | null;
  userId: number | null;
  /** The source document's review state. Null when there is no parsed document. */
  reviewState: DocumentReviewState | null;
  discardedAt: string | null;
  discardReason: string | null;
  createdAt: string;
  /** Status only; leads-api does not send the upstream error text. */
  propagation: Partial<Record<keyof ReferralPropagationState, PropagationStatus>>;
}

export interface OpsReferralListParams {
  page?: number;
  limit?: number;
  search?: string;
  clinicIds?: number[];
  reviewStates?: DocumentReviewState[];
  systemSources?: string[];
  includeDiscarded?: boolean;
  sortOrder?: 'asc' | 'desc';
}

export interface OpsReferralListResponse {
  referrals: OpsReferral[];
  pagination: { page: number; limit: number; total: number; totalPages: number };
}

export interface OpsReferralAuditEntry {
  id: number;
  action: 'create' | 'update' | 'discard' | string;
  changedBy: { id: number | null; name: string | null };
  timestamp: string;
  changes: Array<{ field: string; oldValue: unknown; newValue: unknown }>;
}

export interface OpsReferralAuditHistory {
  referralId: number;
  entries: OpsReferralAuditEntry[];
  /** True when there are more entries than the newest 200 the API returns (leads-api #127). */
  truncated?: boolean;
}
